/**
 * Dev-only override of JWT permissions/roles for FE iteration ahead of
 * backend ROAD-178 populating Descope JWT claims. Tree-shakes from
 * production via `process.env.NODE_ENV === 'production'` early-returns
 * (Next.js statically replaces NODE_ENV in `next build`).
 *
 * The harness is intentionally local-dev-only (`next dev`) — production
 * builds, including staging deploys, run NODE_ENV=production and the
 * helpers no-op. Staging acceptance uses real Descope-issued claims.
 *
 * Usage in `next dev`, paste in the browser console:
 *   __setRbacMockClaims({ permissions: ['accounts:read'], roles: ['view_only'] })
 *   __clearRbacMockClaims()
 *
 * Both helpers reload the page so subscribers re-read localStorage.
 * `__set` REPLACES the stored mock claims wholesale; pass the full
 * { permissions, roles } shape if both should be mocked. Only fields
 * present in the stored object override real JWT claims; unset fields
 * pass through to the real JWT.
 */

const MOCK_CLAIMS_KEY = "rbac_mock_claims";

declare global {
  interface Window {
    __setRbacMockClaims?: (claims: MockRbacClaims) => void;
    __clearRbacMockClaims?: () => void;
  }
}

export interface MockRbacClaims {
  permissions?: string[];
  roles?: string[];
}

/* useSyncExternalStore snapshot. Cached so repeated calls return the
 * same reference unless the underlying localStorage value changed —
 * required so React doesn't re-render in a loop. */
let cachedRaw: string | null = null;
let cachedSnapshot: MockRbacClaims | null = null;

export function getMockClaimsSnapshot(): MockRbacClaims | null {
  if (process.env.NODE_ENV === "production") return null;
  if (typeof window === "undefined") return null;
  // localStorage.getItem can throw (sandboxed iframes, opaque origins,
  // CSP, some incognito modes). This runs during render via
  // useSyncExternalStore in JwtProvider, so an uncaught throw would
  // crash the entire authenticated app. Fail closed → return null.
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(MOCK_CLAIMS_KEY);
  } catch {
    return null;
  }
  if (raw === cachedRaw) return cachedSnapshot;
  cachedRaw = raw;
  try {
    cachedSnapshot = raw ? (JSON.parse(raw) as MockRbacClaims) : null;
  } catch {
    cachedSnapshot = null;
  }
  return cachedSnapshot;
}

export function getMockClaimsServerSnapshot(): MockRbacClaims | null {
  return null;
}

export function subscribeMockClaims(callback: () => void): () => void {
  if (process.env.NODE_ENV === "production") return () => {};
  if (typeof window === "undefined") return () => {};
  // Cross-tab updates only; same-tab updates trigger a full reload from
  // the console helpers below, so no in-tab subscription is needed.
  window.addEventListener("storage", callback);
  return () => window.removeEventListener("storage", callback);
}

export function installMockClaimsConsoleHelpers(): void {
  if (process.env.NODE_ENV === "production") return;
  if (typeof window === "undefined") return;

  window.__setRbacMockClaims = (claims) => {
    try {
      window.localStorage.setItem(MOCK_CLAIMS_KEY, JSON.stringify(claims));
    } catch (error) {
      console.warn("[rbac-mock] localStorage.setItem failed:", error);
      return;
    }
    window.location.reload();
  };
  window.__clearRbacMockClaims = () => {
    try {
      window.localStorage.removeItem(MOCK_CLAIMS_KEY);
    } catch (error) {
      console.warn("[rbac-mock] localStorage.removeItem failed:", error);
      return;
    }
    window.location.reload();
  };
}
